import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  HEADERS,
  COL,
  DEFAULT_CAMPUS_OPTIONS,
  appendRows,
  computeDisplayName,
  displayNameFormula,
  ensureCampusSheet,
  ensureSpreadsheet,
  findRowIndexById,
  mergePhotoFileIds,
  parsePhotoFileIds,
  readAllRows,
  readCampusOptions,
  readWatermarkFileId,
  updateRow,
  writeWatermarkFileId,
} from "../api/sheets";
import {
  driveFileUrl,
  ensureWatermark,
  publicImageUrl,
  resetWatermarkToDefault,
  uploadDispenserPhotos,
  uploadWatermark,
} from "../api/drive";
import { applyWatermark } from "../utils/watermark";
import { getUserEmail } from "../auth/googleAuth";
import PersistentTextarea from "../components/PersistentTextarea";
import MapPicker from "../components/MapPicker";
import { useViewWidth } from "../components/ViewWidthSlider";
import { isFieldworkerMode, useFieldworkerMode } from "../utils/fieldworkerMode";
import { useT } from "../i18n";
import { DriveFile } from "../types/google-api";
import { Row } from "../types/sheets";

interface FormState {
  id: string;
  担当者: string;
  キャンパス: string;
  "部局、施設名": string;
  建物: string;
  階: string;
  設置場所: string;
  備考: string;
  アクセス: string;
  緯度: string;
  経度: string;
}

const emptyForm = (): FormState => ({
  id: "",
  担当者: "",
  キャンパス: "豊中",
  "部局、施設名": "",
  建物: "",
  階: "",
  設置場所: "",
  備考: "",
  アクセス: "",
  緯度: "",
  経度: "",
});

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
    </div>
  );
}

export default function FieldView() {
  const t = useT();
  const fieldworker = useFieldworkerMode();
  const { maxWidth, slider } = useViewWidth("viewWidth.field");
  const [searchParams, setSearchParams] = useSearchParams();
  const [spreadsheetId, setSpreadsheetId] = useState<string | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  // Index of the row currently being edited in place (set by loadRow / deep link). When set,
  // saving updates THAT row even if its ID changed — so assigning an ID to a missing-ID row
  // fixes it in place instead of appending a duplicate.
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);
  // True only when the form was populated from an existing row; gates the safe-merge save so a
  // freshly typed form can never blank out a record the user never loaded.
  const [loaded, setLoaded] = useState(false);
  const [campusOptions, setCampusOptions] = useState(DEFAULT_CAMPUS_OPTIONS);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [existingPhotos, setExistingPhotos] = useState<DriveFile[]>([]);
  const [photos, setPhotos] = useState<File[]>([]);
  const [photoInputKey, setPhotoInputKey] = useState(0);
  const [watermarkId, setWatermarkId] = useState<string | null>(null);
  const [addWatermark, setAddWatermark] = useState(
    () => localStorage.getItem("mew_add_watermark") !== "false"
  );
  const [watermarkBusy, setWatermarkBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [statusTone, setStatusTone] = useState("info");
  const notify = (text: string, tone = "success") => { setStatus(text); setStatusTone(tone); };
  const notifyError = (text: string) => { setStatus(text); setStatusTone("error"); };
  const notifyInfo = (text: string) => { setStatus(text); setStatusTone("info"); };
  const [gpsBusy, setGpsBusy] = useState(false);
  const [gpsAccuracy, setGpsAccuracy] = useState<number | null>(null);
  const gpsWatch = useRef<{ id: number | null; timer: ReturnType<typeof setTimeout> | null; idleTimer?: ReturnType<typeof setTimeout> | null }>({ id: null, timer: null });

  useEffect(() => {
    (async () => {
      try {
        const sid = await ensureSpreadsheet();
        const data = await readAllRows(sid);
        setSpreadsheetId(sid);
        setRows(data);

        // Deep link from the Tasks tab: open the form pre-loaded on a specific row. The handle
        // is the ID when present, otherwise the row's # (populated for ID-less rows), with the
        // array index as a last resort.
        const editId = searchParams.get("editId");
        const editNo = searchParams.get("editNo");
        const editIdx = searchParams.get("editIdx");
        let targetIdx = -1;
        if (editId) targetIdx = findRowIndexById(data, editId);
        else if (editNo) targetIdx = data.findIndex((r) => String(r[COL["#"]] ?? "").trim() === editNo);
        else if (editIdx) {
          const n = parseInt(editIdx, 10);
          if (Number.isFinite(n) && n >= 0 && n < data.length) targetIdx = n;
        }
        if (targetIdx >= 0) {
          loadRow(data[targetIdx], targetIdx);
          setSearchParams({}, { replace: true });
        } else {
          // Default the Assignee to the signed-in user when nothing is loaded.
          const email = getUserEmail();
          if (email) setForm((f) => (f.担当者 ? f : { ...f, 担当者: email }));
        }

        try {
          await ensureCampusSheet(sid);
          const opts = await readCampusOptions(sid);
          if (opts) setCampusOptions(opts);
        } catch (campusErr) { console.warn("Campus options load failed:", campusErr); }

        // Watermark: fieldworkers can't list the shared assets folder under drive.file, so
        // they resolve the (public) watermark by id from the sheet. Owners ensure it and
        // persist the id so it's available to fieldworkers.
        try {
          if (isFieldworkerMode()) {
            const id = await readWatermarkFileId(sid);
            if (id) setWatermarkId(id);
          } else {
            const id = await ensureWatermark();
            setWatermarkId(id);
            writeWatermarkFileId(sid, id).catch((e) => console.warn("persist watermark id:", e));
          }
        } catch (wmErr) { console.warn("watermark resolve:", wmErr); }
      } catch (e) { notifyError(`${t("common.error")}: ${(e as Error).message}`); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function update<K extends keyof FormState>(k: K, v: string) { setForm((f) => ({ ...f, [k]: v })); }

  function clearForm() {
    stopGpsWatch();
    setGpsBusy(false);
    setGpsAccuracy(null);
    // Keep the Assignee (defaults to the signed-in user) for fast batch entry.
    setForm((f) => ({ ...emptyForm(), id: f.id, 担当者: f.担当者 || (getUserEmail() || "") }));
    setEditingRowIndex(null);
    setLoaded(false);
    setExistingPhotos([]);
    setPhotos([]);
    setPhotoInputKey((k) => k + 1);
    notifyInfo(t("field.cleared"));
  }

  const existingIdx = form.id ? findRowIndexById(rows, form.id) : -1;

  // Populates the form from an existing row, remembering it as the edit-in-place target.
  // Empty cells in the loaded row keep whatever is already in the form (e.g. the email-defaulted
  // Assignee) instead of clearing it — protecting against empty overwrites on load.
  function loadRow(r: Row, idx: number) {
    setForm((f) => ({
      id: String(r[COL["ID"]] || ""),
      担当者: String(r[COL["担当者"]] || "") || f.担当者,
      キャンパス: String(r[COL["キャンパス"]] || "") || f.キャンパス || "豊中",
      "部局、施設名": String(r[COL["部局、施設名"]] || "") || f["部局、施設名"],
      建物: String(r[COL["建物"]] || "") || f.建物,
      階: String(r[COL["階"]] || "") || f.階,
      設置場所: String(r[COL["設置場所"]] || "") || f.設置場所,
      備考: String(r[COL["備考"]] || "") || f.備考,
      アクセス: String(r[COL["アクセス"]] || "") || f.アクセス,
      緯度: String(r[COL["緯度"]] || "") || f.緯度,
      経度: String(r[COL["経度"]] || "") || f.経度,
    }));
    setEditingRowIndex(idx);
    setLoaded(true);
    setPhotos([]);
    // Photos are referenced by their Drive file ids stored on the row (写真ファイルID);
    // no folder listing is needed, so this works cross-user under drive.file.
    const fileIds = parsePhotoFileIds(r[COL["写真ファイルID"]]);
    setExistingPhotos(fileIds.map((id, i) => ({ id, name: `${i + 1}`, mimeType: "image/*" })));
    const ref = String(r[COL["ID"]] || "") || `#${String(r[COL["#"]] ?? idx + 1)}`;
    notify(t("field.loadedExisting", { id: ref }));
  }

  function loadExisting() {
    if (existingIdx < 0) return;
    loadRow(rows[existingIdx], existingIdx);
  }

  const GOOD_ACCURACY_M = 10;
  const MAX_SAMPLE_MS = 20000;
  const CONVERGE_MS = 4000;

  function stopGpsWatch() {
    const { id, timer, idleTimer } = gpsWatch.current;
    if (id !== null && navigator.geolocation) navigator.geolocation.clearWatch(id);
    if (timer) clearTimeout(timer);
    if (idleTimer) clearTimeout(idleTimer);
    gpsWatch.current = { id: null, timer: null, idleTimer: null };
  }

  function recordLocation() {
    if (!navigator.geolocation) { notifyError(t("field.geoUnavailable")); return; }
    stopGpsWatch();
    let best: GeolocationPosition | null = null;
    setGpsBusy(true);
    setGpsAccuracy(null);
    notifyInfo(t("field.locating"));

    const finish = (gotFix: boolean) => {
      stopGpsWatch();
      setGpsBusy(false);
      if (gotFix && best) {
        notify(t("field.locationGot", { lat: best.coords.latitude.toFixed(5), lng: best.coords.longitude.toFixed(5), m: String(Math.round(best.coords.accuracy)) }));
      } else if (!best) {
        notifyError(t("field.locationFailed"));
      }
    };

    const onPos = (pos: GeolocationPosition) => {
      if (!best || pos.coords.accuracy < best.coords.accuracy) {
        best = pos;
        update("緯度", pos.coords.latitude.toFixed(6));
        update("経度", pos.coords.longitude.toFixed(6));
        setGpsAccuracy(Math.round(pos.coords.accuracy));
        notifyInfo(t("field.locationSampling", { m: String(Math.round(pos.coords.accuracy)) }));
        if (gpsWatch.current.idleTimer) clearTimeout(gpsWatch.current.idleTimer);
        gpsWatch.current.idleTimer = setTimeout(() => finish(true), CONVERGE_MS);
      }
      if (best.coords.accuracy <= GOOD_ACCURACY_M) finish(true);
    };

    const showFailure = (code: number) =>
      notifyError(code === 1 ? t("field.locationDenied") : t("field.locationFailed"));

    const fallbackLowAccuracy = (hiErr: GeolocationPositionError) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          best = pos;
          update("緯度", pos.coords.latitude.toFixed(6));
          update("経度", pos.coords.longitude.toFixed(6));
          setGpsAccuracy(Math.round(pos.coords.accuracy));
          finish(true);
        },
        (loErr) => {
          console.warn("low-accuracy geolocation also failed", loErr);
          setGpsBusy(false);
          if (!best) showFailure(hiErr.code === 1 || loErr.code === 1 ? 1 : loErr.code);
        },
        { enableHighAccuracy: false, timeout: 15000, maximumAge: 600000 }
      );
    };

    const MAX_GEO_ATTEMPTS = 3;
    const attempt = (n: number) => {
      stopGpsWatch();
      const startedAt = Date.now();
      const onErr = (err: GeolocationPositionError) => {
        console.warn(err);
        stopGpsWatch();
        if (best) { setGpsBusy(false); return; }
        if (err.code === 1) {
          const instant = Date.now() - startedAt < 1000;
          if (instant && n + 1 < MAX_GEO_ATTEMPTS) {
            gpsWatch.current.timer = setTimeout(() => attempt(n + 1), 600);
            return;
          }
          setGpsBusy(false);
          showFailure(1);
          return;
        }
        fallbackLowAccuracy(err);
      };
      gpsWatch.current.id = navigator.geolocation.watchPosition(onPos, onErr, { enableHighAccuracy: true, maximumAge: 0, timeout: 30000 });
      gpsWatch.current.timer = setTimeout(() => finish(true), MAX_SAMPLE_MS);
    };
    attempt(0);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => stopGpsWatch, []);

  function handlePhotos(files: FileList | null) {
    const list = Array.from(files || []);
    setPhotos((prev) => [...prev, ...list]);
  }

  function removePhoto(idx: number) {
    setPhotos((p) => p.filter((_, i) => i !== idx));
  }

  async function handleWatermarkUpload(file: File | null | undefined) {
    if (!file) return;
    setWatermarkBusy(true);
    try {
      const id = await uploadWatermark(file);
      setWatermarkId(id);
      setAddWatermark(true);
      if (spreadsheetId) writeWatermarkFileId(spreadsheetId, id).catch((e) => console.warn("persist watermark id:", e));
      notify(t("field.watermarkUploaded"));
    } catch (err) {
      notifyError(t("field.watermarkUploadFailed", { msg: (err as Error).message }));
    } finally {
      setWatermarkBusy(false);
    }
  }

  async function handleWatermarkReset() {
    if (!window.confirm(t("field.confirmResetWatermark"))) return;
    setWatermarkBusy(true);
    try {
      const id = await resetWatermarkToDefault();
      setWatermarkId(id);
      if (spreadsheetId) writeWatermarkFileId(spreadsheetId, id).catch((e) => console.warn("persist watermark id:", e));
      notify(t("field.watermarkReset"));
    } catch (err) {
      notifyError(t("field.watermarkUploadFailed", { msg: (err as Error).message }));
    } finally {
      setWatermarkBusy(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!spreadsheetId) return;
    if (!form.id) { notifyError(t("field.enterId")); return; }
    // When editing in place (loaded via the ID box or a Tasks deep link), the target is the
    // remembered row even if its ID was changed — so a missing-ID row gets fixed, not duplicated.
    const targetIdx = editingRowIndex != null ? editingRowIndex : findRowIndexById(rows, form.id);
    // Only warn about overwriting when the user did NOT deliberately load the row.
    if (!loaded && targetIdx >= 0) {
      if (!window.confirm(t("field.confirmOverwrite", { id: form.id }))) return;
    }
    setBusy(true);
    notifyInfo(t("common.saving"));
    try {
      const existingRow = targetIdx >= 0 ? rows[targetIdx] : null;
      const prevPhotoIds = existingRow ? existingRow[COL["写真ファイルID"]] : "";

      let newFileIds: string[] = [];
      if (photos.length > 0) {
        let toUpload: Array<File | Blob> = photos;
        if (addWatermark && watermarkId) {
          const wmSrc = publicImageUrl(watermarkId);
          toUpload = await Promise.all(
            photos.map(async (f) => {
              try { return await applyWatermark(f, wmSrc); }
              catch (e) { console.warn("Watermark failed, uploading original:", e); return f; }
            })
          );
        }
        // Continue the filename index after any photos already on the row.
        const existingCount = parsePhotoFileIds(prevPhotoIds).length;
        ({ fileIds: newFileIds } = await uploadDispenserPhotos(form.id, toUpload as File[], existingCount));
      }

      const mergedPhotoIds = mergePhotoFileIds(prevPhotoIds, newFileIds);
      const sheetRowNumber = (targetIdx >= 0 ? targetIdx : rows.length) + 2;
      // Safe merge for editable text fields: a loaded row lets a non-empty form value win and
      // keeps the existing value otherwise; an UN-loaded row never clobbers an existing value,
      // only fills blanks. Never overwrite an existing value unless the row was loaded.
      const pick = (h: string, formVal: string) => {
        const existingVal = existingRow ? String(existingRow[COL[h]] ?? "") : "";
        if (loaded) return formVal.trim() ? formVal : existingVal;
        return existingVal.trim() ? existingVal : formVal;
      };
      const assignee = form.担当者.trim() || (getUserEmail() || "");
      const rowValues: Row = HEADERS.map((h) => {
        if (h === "#") return "";
        if (h === "ID") return form.id;
        if (h === "写真ファイルID") return mergedPhotoIds;
        // 写真フォルダID is no longer written; preserve any legacy value on existing rows.
        if (h === "写真フォルダID") return existingRow ? (existingRow[COL["写真フォルダID"]] || "") : "";
        if (h === "表記名①") return displayNameFormula(sheetRowNumber);
        if (h === "担当者") return pick("担当者", assignee);
        return pick(h, (form as unknown as Record<string, string>)[h] || "");
      });

      if (targetIdx >= 0) {
        rowValues[0] = (existingRow && existingRow[0]) || String(targetIdx + 1);
        await updateRow(spreadsheetId, targetIdx, rowValues);
        setRows((prev) => { const copy = prev.map((r) => [...r] as Row); copy[targetIdx] = rowValues; return copy; });
        notify(t("field.updated", { id: form.id }));
        window.alert(t("field.updated", { id: form.id }));
      } else {
        rowValues[0] = String(rows.length + 1);
        await appendRows(spreadsheetId, [rowValues]);
        setRows((prev) => [...prev, rowValues]);
        notify(t("field.saved", { id: form.id }));
        window.alert(t("field.saved", { id: form.id }));
      }

      setEditingRowIndex(null);
      setLoaded(false);
      setPhotos([]);
      setPhotoInputKey((k) => k + 1);
      setForm((f) => ({ ...emptyForm(), id: "", 担当者: f.担当者 || (getUserEmail() || ""), キャンパス: f.キャンパス, "部局、施設名": f["部局、施設名"], 建物: f.建物, アクセス: f.アクセス }));
    } catch (err) {
      notifyError(`${t("common.error")}: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  const inputCls = "w-full border rounded-md px-2 py-2 focus:outline-none focus:ring-2 focus:ring-pink-300";

  return (
    <>
    {busy && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/60 backdrop-blur-sm">
        <div className="flex flex-col items-center gap-4 select-none">
          <div className="relative">
            <img src={`${process.env.PUBLIC_URL || ""}/assets/logo_128x128.png`} alt="MeW" className="w-16 h-16 opacity-90" />
            <span className="absolute inset-0 -m-2.5 rounded-full border-4 border-pink-200 border-t-pink-600 animate-spin" />
          </div>
          <p className="text-sm text-gray-500 tracking-wide">{t("common.processing")}</p>
        </div>
      </div>
    )}
    <div className="hidden sm:flex justify-end px-4 pt-2">{slider}</div>
    <div className="mx-auto p-4 space-y-4" style={{ maxWidth }}>
      <h2 className="font-bold text-xl flex items-center gap-2 text-gray-800">
        <i className="fa-solid fa-location-crosshairs text-pink-600" aria-hidden="true" />
        {t("field.title")}
      </h2>
      {status && (
        <div className={`text-sm rounded-md px-3 py-2 flex items-start gap-2 border ${statusTone === "error" ? "text-red-700 bg-red-50 border-red-100" : statusTone === "success" ? "text-green-700 bg-green-50 border-green-100" : "text-gray-700 bg-gray-50 border-gray-200"}`}>
          <i className={`fa-solid mt-0.5 ${statusTone === "error" ? "fa-circle-exclamation" : statusTone === "success" ? "fa-circle-check" : "fa-circle-info"}`} aria-hidden="true" />
          <span>{status}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-3">
        <Field label={t("field.id")}>
          <div className="flex gap-2">
            <input list="known-ids" className={inputCls} value={form.id} onChange={(e) => update("id", e.target.value)} />
            <button type="button" onClick={loadExisting} disabled={existingIdx < 0}
              className="shrink-0 inline-flex items-center gap-1 bg-gray-700 hover:bg-gray-800 disabled:opacity-40 text-white rounded-md px-3 py-2 text-sm transition-colors" title={t("field.loadExisting")}>
              <i className="fa-solid fa-download" aria-hidden="true" />
              <span>{t("field.loadExisting")}</span>
            </button>
          </div>
          <datalist id="known-ids">
            {rows.map((r) => {
              const id = String(r[COL["ID"]] || "");
              if (!id) return null;
              const parts = [r[COL["キャンパス"]], r[COL["建物"]], r[COL["部局、施設名"]], r[COL["担当者"]]].filter(Boolean);
              const label = parts.join(" / ");
              return <option key={id} value={id} label={label || undefined} title={label || undefined} />;
            })}
          </datalist>
          {!loaded && existingIdx >= 0 && (
            <p className="mt-1 text-xs text-amber-700 flex items-center gap-1">
              <i className="fa-solid fa-triangle-exclamation" aria-hidden="true" />
              <span>{t("field.idExists")}</span>
            </p>
          )}
        </Field>

        <Field label={t("field.assignee")}>
          <input className={inputCls} value={form.担当者} onChange={(e) => update("担当者", e.target.value)} />
        </Field>

        <Field label={t("field.campus")}>
          <select className={inputCls} value={form.キャンパス} onChange={(e) => update("キャンパス", e.target.value)}>
            {campusOptions.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </select>
        </Field>

        <Field label={t("field.department")}>
          <input className={inputCls} value={form["部局、施設名"]} onChange={(e) => update("部局、施設名", e.target.value)} />
        </Field>

        <div className="grid grid-cols-2 gap-2">
          <Field label={t("field.building")}>
            <input className={inputCls} value={form.建物} onChange={(e) => update("建物", e.target.value)} />
          </Field>
          <Field label={t("field.floor")}>
            <input className={inputCls} value={form.階} onChange={(e) => update("階", e.target.value)} />
          </Field>
        </div>

        <Field label={t("field.installLocation")}>
          <input className={inputCls} value={form.設置場所} onChange={(e) => update("設置場所", e.target.value)} />
        </Field>

        <Field label={t("field.access")}>
          <input className={inputCls} value={form.アクセス} onChange={(e) => update("アクセス", e.target.value)} />
        </Field>

        <Field label={t("field.displayName")}>
          <input className={`${inputCls} bg-gray-100 text-gray-600 cursor-not-allowed`} value={computeDisplayName(form["部局、施設名"], form.階)} readOnly tabIndex={-1} />
        </Field>

        <Field label={t("field.notes")}>
          <PersistentTextarea storageKey="field-notes" className={inputCls} rows={2} value={form.備考} onChange={(e) => update("備考", e.target.value)} />
        </Field>

        <div className="border rounded-md p-3 bg-gray-50 space-y-2">
          <button type="button" onClick={recordLocation} disabled={gpsBusy}
            className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-md px-3 py-3 transition-colors">
            <i className={`fa-solid ${gpsBusy ? "fa-spinner fa-spin" : "fa-location-dot"}`} aria-hidden="true" />
            <span>{gpsBusy ? t("field.locating") : t("field.location")}</span>
          </button>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input placeholder={t("field.latitude")} className={inputCls} value={form.緯度} onChange={(e) => update("緯度", e.target.value)} />
            <input placeholder={t("field.longitude")} className={inputCls} value={form.経度} onChange={(e) => update("経度", e.target.value)} />
          </div>
          {gpsAccuracy !== null && <p className="text-xs text-gray-500">{t("field.accuracy", { m: String(gpsAccuracy) })}</p>}
          <MapPicker lat={form.緯度} lng={form.経度} onChange={(la, ln) => { update("緯度", la); update("経度", ln); }} />
        </div>

        <div className="border rounded-md p-3 bg-gray-50 space-y-2">
          <label className="block text-sm font-medium text-gray-700 flex items-center gap-2">
            <i className="fa-solid fa-camera text-gray-500" aria-hidden="true" />
            {t("field.photos")}
          </label>
          {existingPhotos.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {existingPhotos.map((f) => (
                <a key={f.id} href={driveFileUrl(f.id)} target="_blank" rel="noreferrer" className="block relative group" title={f.name}>
                  <img
                    src={publicImageUrl(f.id, 200)}
                    alt={f.name}
                    className="w-full h-20 object-cover rounded-md border"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 rounded-md transition-colors flex items-center justify-center">
                    <i className="fa-solid fa-arrow-up-right-from-square text-white opacity-0 group-hover:opacity-100 text-lg" aria-hidden="true" />
                  </div>
                </a>
              ))}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="inline-flex items-center justify-center gap-2 cursor-pointer bg-pink-600 hover:bg-pink-700 text-white rounded-md px-3 py-3 transition-colors">
              <i className="fa-solid fa-camera" aria-hidden="true" />
              <span>{t("field.takePhoto")}</span>
              <input key={`cam-${photoInputKey}`} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => handlePhotos(e.target.files)} />
            </label>
            <label className="inline-flex items-center justify-center gap-2 cursor-pointer bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md px-3 py-3 transition-colors">
              <i className="fa-solid fa-images" aria-hidden="true" />
              <span>{t("field.choosePhotos")}</span>
              <input key={`gal-${photoInputKey}`} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handlePhotos(e.target.files)} />
            </label>
          </div>
          <div className="flex flex-col gap-2 border-t pt-2">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input type="checkbox" checked={addWatermark} disabled={!watermarkId} onChange={(e) => { setAddWatermark(e.target.checked); localStorage.setItem("mew_add_watermark", String(e.target.checked)); }} />
              <span>{t("field.addWatermark")}</span>
              {watermarkId && <img src={publicImageUrl(watermarkId, 80)} alt="" className="h-6 ml-1 rounded bg-white border" />}
            </label>
            {/* Fieldworkers use the shared watermark read-only; replacing/resetting it
                writes to the owner's assets folder, so it's owner-only. */}
            {!fieldworker && (
              <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
                <label className="inline-flex items-center gap-1 cursor-pointer hover:text-gray-800">
                  <i className="fa-solid fa-stamp" aria-hidden="true" />
                  <span>{t("field.replaceWatermark")}</span>
                  <input type="file" accept="image/*" disabled={watermarkBusy} className="hidden" onChange={(e) => handleWatermarkUpload(e.target.files && e.target.files[0])} />
                </label>
                <button type="button" onClick={handleWatermarkReset} disabled={watermarkBusy} className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-800 disabled:opacity-50">
                  <i className="fa-solid fa-arrows-rotate" aria-hidden="true" />
                  <span>{t("field.resetWatermark")}</span>
                </button>
              </div>
            )}
          </div>
          {photos.length > 0 && (
            <div className="grid grid-cols-3 gap-2">
              {photos.map((f, i) => (
                <div key={i} className="relative">
                  <img alt="" src={URL.createObjectURL(f)} className="w-full h-20 object-cover rounded-md" />
                  <button type="button" onClick={() => removePhoto(i)} className="absolute top-0 right-0 bg-black/60 hover:bg-black/80 text-white text-xs w-5 h-5 rounded-bl flex items-center justify-center">
                    <i className="fa-solid fa-xmark" aria-hidden="true" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button type="submit" disabled={busy}
            className="inline-flex items-center justify-center gap-2 bg-pink-600 hover:bg-pink-700 text-white rounded-md px-3 py-3 font-bold disabled:opacity-50 transition-colors">
            {busy ? <><i className="fa-solid fa-spinner fa-spin" aria-hidden="true" /><span>{t("common.saving")}</span></> : <><i className="fa-solid fa-floppy-disk" aria-hidden="true" /><span>{t("common.save")}</span></>}
          </button>
          <button type="button" onClick={clearForm} disabled={busy}
            className="inline-flex items-center justify-center gap-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md px-3 py-3 font-bold disabled:opacity-50 transition-colors">
            <i className="fa-solid fa-arrows-rotate" aria-hidden="true" />
            <span>{t("field.clear")}</span>
          </button>
        </div>
      </form>
    </div>
    </>
  );
}
